import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// ถามยืนยันก่อนออกจากหน้าที่ยังมีข้อมูลไม่ได้บันทึก — ครอบสามทาง
//   1. ปิดแท็บ/รีเฟรช     → beforeunload (หน้าต่างของเบราว์เซอร์ ข้อความกำหนดเองไม่ได้)
//   2. ปุ่ม back ของเบราว์เซอร์ → ดันรายการประวัติซ้ำ URL เดิมไว้หนึ่งชั้น พอกด back จึงยังอยู่หน้าเดิมแล้วค่อยถาม
//   3. ลิงก์ในแอป (เมนูข้าง / ปุ่มย้อนกลับ) → ดักคลิกที่ document ช่วง capture ก่อน <Link> ของ React Router
// แอปใช้ <BrowserRouter> ซึ่งไม่มี useBlocker (ต้องเป็น data router) จึงต้องทำเอง
//
// หน้าที่บันทึกเสร็จแล้วพาไปหน้าอื่นเอง ให้เรียก leave(to) ที่ hook คืนมาแทน navigate(to)
// — ไม่ถามซ้ำ และแทนที่ชั้นประวัติที่ดันไว้ ไม่ให้เหลือรายการซ้ำของหน้านี้ค้างให้ back ย้อนมาเจอ
const GUARD_KEY = "__unsavedGuard";

const confirmLeave = (title, text) =>
  Swal.fire({
    icon: "warning",
    title,
    text,
    showCancelButton: true,
    confirmButtonText: "ออกจากหน้านี้",
    cancelButtonText: "อยู่ต่อ",
    confirmButtonColor: "#dc3545",
    reverseButtons: true,
    focusCancel: true,
  }).then((r) => r.isConfirmed);

const pushGuardEntry = () =>
  window.history.pushState({ ...window.history.state, [GUARD_KEY]: true }, "", window.location.href);

export default function useUnsavedGuard(
  when,
  { title = "ยังไม่ได้กดบันทึก", text = "ข้อมูลที่แก้ไว้จะหายถ้าออกจากหน้านี้" } = {}
) {
  const navigate = useNavigate();
  const leavingRef = useRef(false);
  const askingRef = useRef(false);
  const msgRef = useRef({ title, text });
  msgRef.current = { title, text };

  const ask = () => {
    if (askingRef.current) return Promise.resolve(false);
    askingRef.current = true;
    return confirmLeave(msgRef.current.title, msgRef.current.text).finally(() => {
      askingRef.current = false;
    });
  };

  // 1. ปิดแท็บ / รีเฟรช
  useEffect(() => {
    if (!when) return undefined;
    const onBeforeUnload = (e) => {
      if (leavingRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [when]);

  // 2. ปุ่ม back / forward ของเบราว์เซอร์
  useEffect(() => {
    if (!when) return undefined;
    leavingRef.current = false;
    if (!window.history.state?.[GUARD_KEY]) pushGuardEntry();

    const onPop = () => {
      if (leavingRef.current) return;
      // ตอนนี้ถอยมาอยู่รายการเดิมของหน้านี้ (URL เดียวกัน) หน้าจึงยังไม่หายไปไหน
      ask().then((ok) => {
        if (ok) {
          leavingRef.current = true;
          window.history.back();
        } else {
          pushGuardEntry();
        }
      });
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // บันทึกแล้ว (ไม่ได้ออกจากหน้า) — ถอดชั้นที่ดันไว้ ไม่งั้นครูต้องกด back สองครั้ง
      if (!leavingRef.current && window.history.state?.[GUARD_KEY]) window.history.back();
    };
  }, [when]);

  // 3. ลิงก์ภายในแอป
  useEffect(() => {
    if (!when) return undefined;
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest?.("a[href]");
      if (!a || (a.target && a.target !== "_self") || a.hasAttribute("download")) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      // ลิงก์ที่อยู่หน้าเดิม (href="#" ของเมนูพับ ฯลฯ) ไม่ใช่การออกจากหน้า
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;

      e.preventDefault();
      e.stopPropagation();
      ask().then((ok) => {
        if (!ok) return;
        leavingRef.current = true;
        // แทนที่ชั้นที่ดันไว้ ไม่ให้เหลือรายการซ้ำของหน้านี้ค้างในประวัติ
        navigate(url.pathname + url.search + url.hash, { replace: !!window.history.state?.[GUARD_KEY] });
      });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [when, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const leave = useCallback((to, opts = {}) => {
    leavingRef.current = true;
    navigate(to, { ...opts, replace: opts.replace || !!window.history.state?.[GUARD_KEY] });
  }, [navigate]);

  return { leave };
}
